/**
 * Domain — the spine store this slice ships: SearchRun (the observable search
 * run). Entity types carry `urn`; the app owns the streamId↔urn mapping
 * (`urnOf`); appliers are pure folds returning NEW objects. The projection
 * shape is a CLIENT view (a UI cache), distinct from the domain event payloads.
 */

import {
  defineAxVocabulary,
  effect,
  signal,
  type AxVocabulary,
  type CommandTransport,
  type DomainEventEnvelope,
  type ReadonlySignal,
  type StoreSpec,
  type ContextStore,
  type Urn,
} from "@platform/web-kit"

export type SearchRunStatus = "running" | "completed" | "stopped" | "failed"
export type SearchRunStage = "discovering" | "verifying" | "matching" | "enriching" | "done"

/** The client projection of a search run (keyed by its URN). */
export interface SearchRunView {
  urn: Urn
  searchId: string
  status: SearchRunStatus
  stage: SearchRunStage
  discovered: number
  verified: number
  matched: number
  total: number
  startedAt: string
  updatedAt: string
  error?: string
}

/** Read the untrusted wire payload as an indexable record (wire boundary). */
const P = (env: DomainEventEnvelope): Record<string, unknown> => env.payload as Record<string, unknown>
const asUrn = (v: unknown): Urn | undefined => (typeof v === "string" && v.startsWith("urn:") ? (v as Urn) : undefined)
const num = (v: unknown, fallback = 0): number => (typeof v === "number" ? v : fallback)
const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback)

/** The api's SearchRun stream type index (`Agg::career::{userId}::SearchRun::{id}`). */
export const SEARCH_RUN_AGGREGATE = "SearchRun"

/** True for stream ids this app maps onto the SearchRun store. */
export function isSearchRunStream(streamId: string): boolean {
  return streamId.split("::")[3] === SEARCH_RUN_AGGREGATE
}

export function searchRunSpec(): StoreSpec<SearchRunView> {
  return {
    name: "search-run",
    urnOf: (env) => asUrn(P(env)["searchId"]),
    appliers: {
      "searchRun.Started": (_current, env) => {
        const p = P(env)
        const urn = asUrn(p["searchId"])
        if (!urn) return undefined
        const at = str(p["startedAt"])
        return {
          urn,
          searchId: urn,
          status: "running",
          stage: "discovering",
          discovered: 0,
          verified: 0,
          matched: 0,
          total: 0,
          startedAt: at,
          updatedAt: at,
        }
      },
      "searchRun.DiscoveryProgressed": (current, env) => {
        if (!current) return current
        const p = P(env)
        return { ...current, discovered: num(p["discovered"], current.discovered), updatedAt: str(p["at"], current.updatedAt) }
      },
      "searchRun.Progressed": (current, env) => {
        if (!current) return current
        const p = P(env)
        return {
          ...current,
          stage: str(p["stage"], current.stage) as SearchRunStage,
          verified: num(p["verified"], current.verified),
          matched: num(p["matched"], current.matched),
          total: num(p["total"], current.total),
          updatedAt: str(p["at"], current.updatedAt),
        }
      },
      "searchRun.Stopped": (current, env) =>
        current ? { ...current, status: "stopped", stage: "done", updatedAt: str(P(env)["at"], current.updatedAt) } : current,
      "searchRun.Completed": (current, env) =>
        current ? { ...current, status: "completed", stage: "done", updatedAt: str(P(env)["at"], current.updatedAt) } : current,
      "searchRun.Failed": (current, env) => {
        if (!current) return current
        const p = P(env)
        return { ...current, status: "failed", stage: "done", error: str(p["error"], "unknown"), updatedAt: str(p["at"], current.updatedAt) }
      },
    },
    projections: {
      // Most recently started first (stable, content-wise memoized by the store).
      aktiv: (entities) => [...entities.values()].sort((a, b) => b.startedAt.localeCompare(a.startedAt)),
    },
  }
}

export type SearchRunStore = ContextStore<SearchRunView>

// ─── Ingest (onboarding run) — push-fed live run status ──────────────────────

export type IngestStatus = "pending" | "extracting" | "proposed" | "confirmed" | "failed"

/** The client projection of an ingest run (keyed by its URN). */
export interface IngestRunView {
  urn: Urn
  status: IngestStatus
  documents: string[]
  claimCount: number
  assetCount: number
  profileSeeded: boolean
  updatedAt: string
}

export const INGEST_AGGREGATE = "Ingest"
export const EVIDENCE_AGGREGATE = "Evidence"
export function isIngestStream(streamId: string): boolean {
  return streamId.split("::")[3] === INGEST_AGGREGATE
}
export function isEvidenceStream(streamId: string): boolean {
  return streamId.split("::")[3] === EVIDENCE_AGGREGATE
}

/** Client-side career stream-id grammar (`Agg::career::{userId}::{Aggregate}[::{id}]`). */
export function careerStream(userId: string, aggregate: string, id?: string): string {
  return id ? `Agg::career::${userId}::${aggregate}::${id}` : `Agg::career::${userId}::${aggregate}`
}

/** The Ingest run URN for a run id. */
export const ingestRunUrn = (ingestId: string): Urn => `urn:career:ingest:${ingestId}`

/** The Ingest stream URN for a run (`Agg::career::{userId}::Ingest::{urn}`). */
function ingestUrnOf(streamId: string): Urn | undefined {
  const rest = streamId.split("::").slice(4).join("::")
  return rest.startsWith("urn:") ? (rest as Urn) : undefined
}

export function ingestSpec(): StoreSpec<IngestRunView> {
  const blank = (urn: Urn, at: string): IngestRunView => ({
    urn,
    status: "pending",
    documents: [],
    claimCount: 0,
    assetCount: 0,
    profileSeeded: false,
    updatedAt: at,
  })
  return {
    name: "ingest",
    urnOf: (env) => ingestUrnOf(env.streamId),
    appliers: {
      "ingest.DocumentUploaded": (current, env) => {
        const urn = ingestUrnOf(env.streamId)
        if (!urn) return current
        const p = P(env)
        const doc = p["document"] as { id?: string } | undefined
        const base = current ?? blank(urn, str(p["uploadedAt"]))
        return { ...base, documents: doc?.id ? [...base.documents, doc.id] : base.documents, updatedAt: str(p["uploadedAt"], base.updatedAt) }
      },
      "ingest.RunStarted": (current, env) => (current ? { ...current, status: "extracting" } : current),
      "ingest.ProposalsReady": (current, env) => {
        if (!current) return current
        const p = P(env)
        const counts = (p["counts"] as { claims?: number; assets?: number } | undefined) ?? {}
        return {
          ...current,
          status: "proposed",
          claimCount: num(counts.claims, current.claimCount),
          assetCount: num(counts.assets, current.assetCount),
          profileSeeded: Boolean(p["profileSeeded"]),
        }
      },
      "ingest.Confirmed": (current) => (current ? { ...current, status: "confirmed" } : current),
    },
    projections: {
      aktiv: (entities) => [...entities.values()].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    },
  }
}

export type IngestStore = ContextStore<IngestRunView>

// ─── Evidence (claim ledger) — push-fed replay + optimistic confirm/edit/discard ─

export type EvidenceGrade = "evidenced" | "self-positioning" | "gap"
export type ClaimStatus = "proposed" | "confirmed"

/** A tag as authored (facet + label). */
export interface ClaimTag {
  facet: string
  label: string
}

/** The client projection of an evidence claim (keeps sources/tags whole so an edit round-trips). */
export interface EvidenceClaimView {
  urn: Urn
  id: string
  statement: string
  grade: EvidenceGrade
  status: ClaimStatus
  origin: string
  /** Full source refs — preserved so an edit re-submits them (grade re-derived server-side). */
  sources: unknown[]
  tags: ClaimTag[]
}

const claimOf = (env: DomainEventEnvelope): { id?: string; statement?: string; grade?: string; status?: string; origin?: string; sources?: unknown[]; tags?: ClaimTag[] } | undefined =>
  P(env)["claim"] as never

function toClaimView(raw: NonNullable<ReturnType<typeof claimOf>>): EvidenceClaimView | undefined {
  const id = raw.id
  if (!id) return undefined
  return {
    urn: id as Urn,
    id,
    statement: str(raw.statement),
    grade: (raw.grade ?? "gap") as EvidenceGrade,
    status: (raw.status ?? "proposed") as ClaimStatus,
    origin: str(raw.origin, "ingested"),
    sources: Array.isArray(raw.sources) ? raw.sources : [],
    tags: Array.isArray(raw.tags) ? raw.tags.filter((t): t is ClaimTag => Boolean(t?.label)) : [],
  }
}

/** Command types the evidence store issues optimistically (names match the api verbs / AX). */
export type EvidenceCommand =
  | { type: "confirm-claim"; payload: { claimId: string } }
  | { type: "discard-claim"; payload: { claimId: string; reason?: string } }

export function evidenceSpec(transport: CommandTransport): StoreSpec<EvidenceClaimView> {
  return {
    name: "evidence",
    transport,
    urnOf: (env) => {
      const raw = claimOf(env)
      if (raw?.id) return raw.id as Urn
      const claimId = P(env)["claimId"]
      return typeof claimId === "string" ? (claimId as Urn) : undefined
    },
    appliers: {
      "claim.Proposed": (_current, env) => {
        const raw = claimOf(env)
        return raw ? toClaimView(raw) : undefined
      },
      "claim.Edited": (current, env) => {
        const raw = claimOf(env)
        return raw ? (toClaimView(raw) ?? current) : current
      },
      // ABSOLUTE end-state: confirm SETS status="confirmed" (never a relative toggle).
      "claim.Confirmed": (current) => (current ? { ...current, status: "confirmed" } : current),
      "claim.SourceResolutionFailed": (current, env) => {
        if (!current) return current
        const to = str(P(env)["downgradedTo"]) as EvidenceGrade
        return to ? { ...current, grade: to } : current
      },
      "claim.Discarded": () => undefined, // tombstone → removed
    },
    projections: {
      alle: (entities) => [...entities.values()].sort((a, b) => a.id.localeCompare(b.id)),
      offen: (entities) => [...entities.values()].filter((c) => c.status === "proposed").sort((a, b) => a.id.localeCompare(b.id)),
      bestaetigt: (entities) => [...entities.values()].filter((c) => c.status === "confirmed").sort((a, b) => a.id.localeCompare(b.id)),
    },
    optimistic: {
      // Optimistic prediction carries the ABSOLUTE end-state the reconciling event sets.
      "confirm-claim": (entities, cmd) => {
        const { claimId } = (cmd as EvidenceCommand & { type: "confirm-claim" }).payload
        const current = entities.get(claimId as Urn)
        return current ? [{ urn: claimId as Urn, next: { ...current, status: "confirmed" as const } }] : []
      },
      "discard-claim": (entities, cmd) => {
        const { claimId } = (cmd as EvidenceCommand & { type: "discard-claim" }).payload
        return entities.has(claimId as Urn) ? [{ urn: claimId as Urn, next: undefined }] : []
      },
    },
  }
}

export type EvidenceStore = ContextStore<EvidenceClaimView>

// ─── Asset (typed inventory) — fetch-hydrated + optimistic confirm/remove ─────

export type AssetKind =
  | "certificate" | "reference" | "diploma" | "work_certificate" | "portrait" | "portfolio" | "other"
export type AssetStatus = "proposed" | "confirmed"

/** The client projection of an inventory asset (keyed by its URN). */
export interface AssetView {
  urn: Urn
  id: string
  kind: AssetKind
  title: string
  status: AssetStatus
  issuer?: string
  fileKey: string
  sourceDocumentId?: string
}

export const ASSET_AGGREGATE = "Asset"
export function isAssetStream(streamId: string): boolean {
  return streamId.split("::")[3] === ASSET_AGGREGATE
}

const assetOf = (env: DomainEventEnvelope): Record<string, unknown> | undefined =>
  P(env)["asset"] as Record<string, unknown> | undefined

function toAssetView(raw: Record<string, unknown>): AssetView | undefined {
  const id = raw["id"]
  if (typeof id !== "string") return undefined
  const file = raw["file"] as { key?: string } | undefined
  return {
    urn: id as Urn,
    id,
    kind: (raw["kind"] as AssetKind) ?? "other",
    title: str(raw["title"]),
    status: (raw["status"] as AssetStatus) ?? "proposed",
    ...(typeof raw["issuer"] === "string" ? { issuer: raw["issuer"] } : {}),
    fileKey: str(file?.key),
    ...(typeof raw["sourceDocumentId"] === "string" ? { sourceDocumentId: raw["sourceDocumentId"] } : {}),
  }
}

/** Command types the asset store issues optimistically (names match the api verbs / AX). */
export type AssetCommand =
  | { type: "confirm-asset"; payload: { assetId: string } }
  | { type: "remove-asset"; payload: { assetId: string; reason?: string } }

export function assetSpec(transport: CommandTransport): StoreSpec<AssetView> {
  return {
    name: "asset",
    transport,
    urnOf: (env) => {
      const raw = assetOf(env)
      if (raw && typeof raw["id"] === "string") return raw["id"] as Urn
      const assetId = P(env)["assetId"]
      return typeof assetId === "string" ? (assetId as Urn) : undefined
    },
    appliers: {
      "asset.Added": (_current, env) => {
        const raw = assetOf(env)
        return raw ? toAssetView(raw) : undefined
      },
      "asset.PromotedFromDocument": (_current, env) => {
        const raw = assetOf(env)
        return raw ? toAssetView(raw) : undefined
      },
      "asset.Updated": (current, env) => {
        const raw = assetOf(env)
        return raw ? (toAssetView(raw) ?? current) : current
      },
      // ABSOLUTE end-state: confirm SETS status="confirmed" (never a relative toggle).
      "asset.Confirmed": (current) => (current ? { ...current, status: "confirmed" } : current),
      "asset.Removed": () => undefined, // tombstone → removed
    },
    projections: {
      alle: (entities) => [...entities.values()].sort((a, b) => a.title.localeCompare(b.title, "de")),
      offen: (entities) => [...entities.values()].filter((a) => a.status === "proposed").sort((a, b) => a.title.localeCompare(b.title, "de")),
      bestaetigt: (entities) => [...entities.values()].filter((a) => a.status === "confirmed").sort((a, b) => a.title.localeCompare(b.title, "de")),
    },
    optimistic: {
      // Optimistic prediction carries the ABSOLUTE end-state the reconciling event sets.
      "confirm-asset": (entities, cmd) => {
        const { assetId } = (cmd as AssetCommand & { type: "confirm-asset" }).payload
        const current = entities.get(assetId as Urn)
        return current ? [{ urn: assetId as Urn, next: { ...current, status: "confirmed" as const } }] : []
      },
      "remove-asset": (entities, cmd) => {
        const { assetId } = (cmd as AssetCommand & { type: "remove-asset" }).payload
        return entities.has(assetId as Urn) ? [{ urn: assetId as Urn, next: undefined }] : []
      },
    },
  }
}

export type AssetStore = ContextStore<AssetView>

// ─── Workshop (W1/W2/W3) — push-fed live status for the list ──────────────────

export type WorkshopStatus = "in_progress" | "completed"

/** A lean client projection of a workshop's live status (the LIST reconciles the
 *  rich fetched summary with this so gate/status flips arrive without a reload). The
 *  runner itself is fetch-driven off the full server WorkshopView (durable truth). */
export interface WorkshopStatusView {
  urn: Urn
  type: string
  status: WorkshopStatus
  hasContext: boolean
  updatedAt: string
}

export const WORKSHOP_AGGREGATE = "Workshop"
export function isWorkshopStream(streamId: string): boolean {
  return streamId.split("::")[3] === WORKSHOP_AGGREGATE
}

export function workshopSpec(): StoreSpec<WorkshopStatusView> {
  return {
    name: "workshop",
    urnOf: (env) => asUrn(P(env)["workshopId"]),
    appliers: {
      "workshop.Started": (_current, env) => {
        const p = P(env)
        const urn = asUrn(p["workshopId"])
        if (!urn) return undefined
        return { urn, type: str(p["type"]), status: "in_progress", hasContext: false, updatedAt: str(p["at"]) }
      },
      "workshop.ContextProduced": (current, env) =>
        current ? { ...current, hasContext: true, updatedAt: str(P(env)["at"], current.updatedAt) } : current,
      // ABSOLUTE end-state: completion SETS status="completed".
      "workshop.Completed": (current, env) =>
        current ? { ...current, status: "completed", updatedAt: str(P(env)["at"], current.updatedAt) } : current,
    },
    projections: {
      alle: (entities) => [...entities.values()].sort((a, b) => a.type.localeCompare(b.type)),
    },
  }
}

export type WorkshopStore = ContextStore<WorkshopStatusView>

// ─── Opportunity (search results) — fetch-reconciled + optimistic verdicts ────

/** The shared MatchTier vocabulary (definition-v2 `MatchTier`). */
export type MatchTier = "strong" | "viable" | "weak" | "unsuited"
/** The lifecycle stage an opportunity has reached (drives the `is-*` card state). */
export type OpportunityStage = "discovered" | "verified" | "briefed" | "matched"
export type JobState = "open" | "closed" | "unknown"
export type VerifyFit = "match" | "borderline" | "mismatch"
export type AddressRegister = "du" | "Sie"

/** One per-dimension match signal — strength + verbatim quotes + named gaps (RAW). */
export interface MatchSignalView {
  dimension: string
  strength: number
  quotes: string[]
  gaps: string[]
}

/** The presented German brief (once authored) with its honesty flags. */
export interface OpportunityBriefView {
  brief: string
  fitNote?: string
  degraded: boolean
  provenanceCount: number
}

/** One graded employer signal shown in a result's disclosed "Arbeitgeber" section (E3b). */
export interface OrgEvidenceView {
  /** The source kind — kununu | trustpilot | company | news | posting | … (open string). */
  kind: string
  /** Where it came from (a source name / host / oracle). */
  source: string
  /** The graded claim itself. */
  claim: string
  url?: string
}

/** The compact employer digest carried ON a search result (E3b): one-line summary + capped signals. */
export interface OrgDigestView {
  orgId: string
  name: string
  /** The synthesized one-line German org summary (collapsed default), when authored. */
  summary?: string
  evidence: OrgEvidenceView[]
}

/** The client projection of one discovered opportunity (keyed by its URN). */
export interface OpportunityCardView {
  urn: Urn
  id: string
  employer: string
  title: string
  location?: string
  url?: string
  snippet?: string
  /** The normalized employer slug linking to the Organization stream (deep-link). */
  orgRef?: string
  stage: OpportunityStage
  jobState?: JobState
  fit?: VerifyFit
  salary?: string
  register?: AddressRegister
  brief?: OpportunityBriefView
  /** The enriched employer understanding for this result's org (E3b), when the read model carries it. */
  org?: OrgDigestView
  tier?: MatchTier
  /** 0..1 match score (rendered as a human percentage — AX-6). */
  score?: number
  signals: MatchSignalView[]
  bookmarked: boolean
  dismissed: boolean
  createdAt: string
}

export const OPPORTUNITY_AGGREGATE = "Opportunity"
export const ORGANIZATION_AGGREGATE = "Organization"

/** True for stream ids this app maps onto the opportunity store. */
export function isOpportunityStream(streamId: string): boolean {
  return streamId.split("::")[3] === OPPORTUNITY_AGGREGATE
}
/** True for stream ids this app maps onto the organization store. */
export function isOrganizationStream(streamId: string): boolean {
  return streamId.split("::")[3] === ORGANIZATION_AGGREGATE
}

const asTier = (v: unknown): MatchTier | undefined =>
  v === "strong" || v === "viable" || v === "weak" || v === "unsuited" ? v : undefined
const asRegister = (v: unknown): AddressRegister | undefined => (v === "du" || v === "Sie" ? v : undefined)
const asJobState = (v: unknown): JobState | undefined =>
  v === "open" || v === "closed" || v === "unknown" ? v : undefined
const asFit = (v: unknown): VerifyFit | undefined =>
  v === "match" || v === "borderline" || v === "mismatch" ? v : undefined

/** Highest reached stage between two — matched > briefed > verified > discovered. */
const STAGE_RANK: Record<OpportunityStage, number> = { discovered: 0, verified: 1, briefed: 2, matched: 3 }
const maxStage = (a: OpportunityStage, b: OpportunityStage): OpportunityStage => (STAGE_RANK[a] >= STAGE_RANK[b] ? a : b)

function toBriefView(raw: Record<string, unknown> | undefined | null): OpportunityBriefView | undefined {
  if (!raw || typeof raw["brief"] !== "string") return undefined
  const meta = raw["meta"] as { degraded?: unknown } | undefined
  return {
    brief: str(raw["brief"]),
    ...(typeof raw["fitNote"] === "string" ? { fitNote: raw["fitNote"] } : {}),
    degraded: Boolean(meta?.degraded),
    provenanceCount: Array.isArray(raw["provenance"]) ? raw["provenance"].length : 0,
  }
}

function toSignals(raw: unknown): MatchSignalView[] {
  if (!Array.isArray(raw)) return []
  return raw.map((s) => {
    const sig = (s ?? {}) as Record<string, unknown>
    return {
      dimension: str(sig["dimension"], "fit"),
      strength: num(sig["strength"]),
      quotes: Array.isArray(sig["quotes"]) ? sig["quotes"].filter((q): q is string => typeof q === "string") : [],
      gaps: Array.isArray(sig["gaps"]) ? sig["gaps"].filter((g): g is string => typeof g === "string") : [],
    }
  })
}

/** Project the read-model employer digest (E3b) into the card's org view; undefined when absent. */
function toOrgDigest(raw: unknown): OrgDigestView | undefined {
  if (!raw || typeof raw !== "object") return undefined
  const d = raw as Record<string, unknown>
  if (typeof d["orgId"] !== "string" || typeof d["name"] !== "string") return undefined
  const evidence = Array.isArray(d["evidence"])
    ? d["evidence"].flatMap((e): OrgEvidenceView[] => {
        const ev = (e ?? {}) as Record<string, unknown>
        if (typeof ev["kind"] !== "string" || typeof ev["source"] !== "string" || typeof ev["claim"] !== "string") return []
        return [{ kind: ev["kind"], source: ev["source"], claim: ev["claim"], ...(typeof ev["url"] === "string" ? { url: ev["url"] } : {}) }]
      })
    : []
  return { orgId: d["orgId"], name: d["name"], ...(typeof d["summary"] === "string" ? { summary: d["summary"] } : {}), evidence }
}

/** Build a card view from a full Opportunity object, preserving any Match from `current` and an optional org digest. */
function toOpportunityCard(opp: Record<string, unknown>, current?: OpportunityCardView, org?: OrgDigestView): OpportunityCardView | undefined {
  const id = opp["id"]
  if (typeof id !== "string") return undefined
  const posting = (opp["posting"] as Record<string, unknown> | undefined) ?? {}
  const verification = opp["verification"] as Record<string, unknown> | null | undefined
  const brief = toBriefView(opp["brief"] as Record<string, unknown> | null | undefined)
  const orgDigest = org ?? current?.org // a live Discovered (no digest) keeps the fetch-reconciled one
  const hasMatch = Boolean(current?.tier)
  const stage: OpportunityStage = hasMatch ? "matched" : brief ? "briefed" : verification ? "verified" : "discovered"
  return {
    urn: id as Urn,
    id,
    employer: str(posting["employer"]),
    title: str(posting["title"]),
    ...(typeof posting["location"] === "string" ? { location: posting["location"] } : {}),
    ...(typeof posting["url"] === "string" ? { url: posting["url"] } : {}),
    ...(typeof posting["snippet"] === "string" ? { snippet: posting["snippet"] } : {}),
    ...(typeof opp["orgRef"] === "string" ? { orgRef: opp["orgRef"] } : {}),
    stage,
    ...(verification ? cardVerify(verification) : {}),
    ...(brief ? { brief } : {}),
    ...(orgDigest ? { org: orgDigest } : {}),
    ...(current?.tier ? { tier: current.tier } : {}),
    ...(typeof current?.score === "number" ? { score: current.score } : {}),
    signals: current?.signals ?? [],
    bookmarked: Boolean(opp["bookmarked"]),
    dismissed: Boolean(opp["dismissed"]),
    createdAt: str(opp["createdAt"]),
  }
}

/** The subset of verify fields the card surfaces. */
function cardVerify(v: Record<string, unknown>): Partial<OpportunityCardView> {
  return {
    ...(asJobState(v["jobState"]) ? { jobState: asJobState(v["jobState"]) } : {}),
    ...(asFit(v["fit"]) ? { fit: asFit(v["fit"]) } : {}),
    ...(typeof v["salary"] === "string" ? { salary: v["salary"] } : {}),
    ...(asRegister(v["register"]) ? { register: asRegister(v["register"]) } : {}),
  }
}

/** Commands the opportunity store issues optimistically (names match the AX verbs). */
export type OpportunityCommand =
  | { type: "bookmark"; payload: { opportunityId: string } }
  | { type: "dismiss"; payload: { opportunityId: string; reason?: string } }
  | { type: "delete"; payload: { opportunityId: string } }

export function opportunitySpec(transport: CommandTransport): StoreSpec<OpportunityCardView> {
  return {
    name: "opportunity",
    transport,
    urnOf: (env) => {
      const opp = P(env)["opportunity"] as Record<string, unknown> | undefined
      if (opp && typeof opp["id"] === "string") return opp["id"] as Urn
      const id = P(env)["opportunityId"]
      return typeof id === "string" ? (id as Urn) : undefined
    },
    appliers: {
      "opportunity.Discovered": (current, env) => {
        const opp = P(env)["opportunity"] as Record<string, unknown> | undefined
        // E3b: the fetch-reconciled frame carries the employer digest alongside the opportunity.
        const org = toOrgDigest(P(env)["org"])
        return opp ? (toOpportunityCard(opp, current, org) ?? current) : current
      },
      "opportunity.Verified": (current, env) => {
        if (!current) return current
        const v = P(env)["verification"] as Record<string, unknown> | undefined
        if (!v) return current
        return { ...current, stage: maxStage(current.stage, "verified"), ...cardVerify(v) }
      },
      "opportunity.Briefed": (current, env) => {
        if (!current) return current
        const brief = toBriefView(P(env)["brief"] as Record<string, unknown> | undefined)
        return brief ? { ...current, stage: maxStage(current.stage, "briefed"), brief } : current
      },
      "opportunity.Matched": (current, env) => {
        if (!current) return current
        const m = P(env)["match"] as Record<string, unknown> | undefined
        if (!m) return current
        return {
          ...current,
          stage: "matched",
          ...(asTier(m["tier"]) ? { tier: asTier(m["tier"]) } : {}),
          score: num(m["score"], current.score ?? 0),
          signals: toSignals(m["signals"]),
        }
      },
      // ABSOLUTE end-state for user verdicts (a Bookmarked payload SETS the flag).
      "opportunity.Bookmarked": (current, env) =>
        current ? { ...current, bookmarked: Boolean(P(env)["bookmarked"]) } : current,
      "opportunity.Dismissed": (current) => (current ? { ...current, dismissed: true } : current),
      "opportunity.Deleted": () => undefined, // tombstone → removed
    },
    projections: {
      // Newest first; dismissed opportunities leave the main list (still fetchable).
      alle: (entities) =>
        [...entities.values()]
          .filter((o) => !o.dismissed)
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id)),
      gemerkt: (entities) =>
        [...entities.values()]
          .filter((o) => o.bookmarked && !o.dismissed)
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id)),
    },
    optimistic: {
      // Optimistic prediction carries the ABSOLUTE end-state the reconciling event sets.
      bookmark: (entities, cmd) => {
        const { opportunityId } = (cmd as OpportunityCommand & { type: "bookmark" }).payload
        const current = entities.get(opportunityId as Urn)
        return current ? [{ urn: opportunityId as Urn, next: { ...current, bookmarked: true } }] : []
      },
      dismiss: (entities, cmd) => {
        const { opportunityId } = (cmd as OpportunityCommand & { type: "dismiss" }).payload
        const current = entities.get(opportunityId as Urn)
        return current ? [{ urn: opportunityId as Urn, next: { ...current, dismissed: true } }] : []
      },
      delete: (entities, cmd) => {
        const { opportunityId } = (cmd as OpportunityCommand & { type: "delete" }).payload
        return entities.has(opportunityId as Urn) ? [{ urn: opportunityId as Urn, next: undefined }] : []
      },
    },
  }
}

export type OpportunityStore = ContextStore<OpportunityCardView>

// ─── Organization (employer dossier) — push/fetch-fed summary ─────────────────

/** The client projection of an org dossier (keyed by a minted client URN). */
export interface OrgSummaryView {
  urn: Urn
  slug: string
  name: string
  summaryBody?: string
  summaryProvenanceCount: number
  register?: AddressRegister
  evidenceCount: number
  /** True when the summary is the deterministic composer fallback, not authored prose. */
  degraded: boolean
}

/** The client URN for an org slug (org ids are slugs, not urns → mint one for AX-4). */
export const orgUrn = (slug: string): Urn => `urn:career:org:${slug}`

/** Resolve the org URN from a `…::Organization::{slug}` stream id. */
function orgUrnOf(streamId: string): Urn | undefined {
  const slug = streamId.split("::").slice(4).join("::")
  return slug ? orgUrn(slug) : undefined
}

export function orgSpec(): StoreSpec<OrgSummaryView> {
  const blank = (urn: Urn, slug: string): OrgSummaryView => ({
    urn,
    slug,
    name: "",
    summaryProvenanceCount: 0,
    evidenceCount: 0,
    degraded: false,
  })
  return {
    name: "organization",
    urnOf: (env) => orgUrnOf(env.streamId),
    appliers: {
      "organization.Observed": (current, env) => {
        const urn = orgUrnOf(env.streamId)
        if (!urn) return current
        const slug = env.streamId.split("::").slice(4).join("::")
        const p = P(env)
        const evidence = Array.isArray(p["evidence"]) ? p["evidence"].length : 0
        const base = current ?? blank(urn, slug)
        return { ...base, name: str(p["name"], base.name), evidenceCount: Math.max(base.evidenceCount, evidence) }
      },
      "organization.Researched": (current, env) => {
        if (!current) return current
        const p = P(env)
        const evidence = Array.isArray(p["evidence"]) ? p["evidence"].length : 0
        return {
          ...current,
          evidenceCount: Math.max(current.evidenceCount, evidence),
          ...(asRegister(p["register"]) ? { register: asRegister(p["register"]) } : {}),
        }
      },
      "organization.Summarized": (current, env) => {
        if (!current) return current
        const s = P(env)["summary"] as Record<string, unknown> | undefined
        if (!s) return current
        const meta = s["meta"] as { degraded?: unknown } | undefined
        return {
          ...current,
          summaryBody: str(s["body"]),
          summaryProvenanceCount: Array.isArray(s["provenance"]) ? s["provenance"].length : 0,
          degraded: Boolean(meta?.degraded),
        }
      },
    },
    projections: {
      alle: (entities) => [...entities.values()].sort((a, b) => a.slug.localeCompare(b.slug)),
    },
  }
}

export type OrgStore = ContextStore<OrgSummaryView>

// ─── GenerationRun (application generation) — push-fed live rail (mirrors SearchRun) ─

export type GenerationRunStatus = "running" | "completed" | "stopped" | "failed"
export type GenerationRunStage = "preselecting" | "authoring" | "judging" | "kurzprofil" | "tailoring" | "emailing" | "done"

/** The client projection of a generation run (keyed by its URN). */
export interface GenerationRunView {
  urn: Urn
  applicationRef: string
  status: GenerationRunStatus
  stage: GenerationRunStage
  iterations: number
  converged?: boolean
  costUsd: number
  startedAt: string
  updatedAt: string
  error?: string
}

export const GENERATION_RUN_AGGREGATE = "GenerationRun"
export function isGenerationRunStream(streamId: string): boolean {
  return streamId.split("::")[3] === GENERATION_RUN_AGGREGATE
}

export function generationRunSpec(): StoreSpec<GenerationRunView> {
  return {
    name: "generation-run",
    urnOf: (env) => asUrn(P(env)["generationId"]),
    appliers: {
      "generationRun.Started": (_current, env) => {
        const p = P(env)
        const urn = asUrn(p["generationId"])
        if (!urn) return undefined
        const at = str(p["startedAt"])
        return { urn, applicationRef: str(p["applicationRef"]), status: "running", stage: "preselecting", iterations: 0, costUsd: 0, startedAt: at, updatedAt: at }
      },
      "generationRun.Progressed": (current, env) => {
        if (!current) return current
        const p = P(env)
        return {
          ...current,
          stage: str(p["stage"], current.stage) as GenerationRunStage,
          iterations: num(p["iterations"], current.iterations),
          costUsd: num(p["costUsd"], current.costUsd),
          updatedAt: str(p["at"], current.updatedAt),
        }
      },
      "generationRun.Stopped": (current, env) =>
        current ? { ...current, status: "stopped", stage: "done", updatedAt: str(P(env)["at"], current.updatedAt) } : current,
      "generationRun.Completed": (current, env) => {
        if (!current) return current
        const p = P(env)
        return { ...current, status: "completed", stage: "done", converged: Boolean(p["converged"]), costUsd: num(p["costUsd"], current.costUsd), updatedAt: str(p["at"], current.updatedAt) }
      },
      "generationRun.Failed": (current, env) => {
        if (!current) return current
        const p = P(env)
        return { ...current, status: "failed", stage: "done", error: str(p["error"], "unknown"), updatedAt: str(p["at"], current.updatedAt) }
      },
    },
    projections: {
      aktiv: (entities) => [...entities.values()].sort((a, b) => b.startedAt.localeCompare(a.startedAt)),
    },
  }
}

export type GenerationRunStore = ContextStore<GenerationRunView>

// ─── Application (the product artifact) — fetch-seeded + push-fed + optimistic ─────

export type ApplicationStatus = "draft" | "in_review" | "changes_requested" | "approved" | "sent"

export interface ProvenanceEntryView { anchor: string; refs: string[] }
export interface ApplicationGateView { gate: string; pass: boolean; notes?: string }
export interface ApplicationAnnotationView { gate: string; severity: string; kind: string; comment: string; span?: string }
export interface ApplicationEditView { id: string; targetKind: string; paragraphIndex?: number; section?: string; before: string; after: string; status: string; rationale?: string }
export interface ApplicationTailoringSectionView { section: string; include: boolean; order?: number; emphasis?: string; rewrite?: string }
export interface ApplicationAttachmentView { assetRef: string; include: boolean; rationale?: string }
export interface ApplicationSmartLinkView { url: string; kind: string; include: boolean; label?: string }

/** The client projection of one application — the review surface renders directly from this. */
export interface ApplicationView {
  urn: Urn
  id: string
  status: ApplicationStatus
  opportunityRef?: string
  letterBody: string
  letterProvenance: ProvenanceEntryView[]
  letterSubject?: string
  letterSalutation?: string
  letterClosing?: string
  letterEnclosure?: string
  letterDegraded: boolean
  emailNote?: string
  kurzprofilBody?: string
  kurzprofilProvenance: ProvenanceEntryView[]
  kurzprofilDegraded: boolean
  gates: ApplicationGateView[]
  annotations: ApplicationAnnotationView[]
  converged?: boolean
  iterations?: number
  stopReason?: string
  tailoringSections: ApplicationTailoringSectionView[]
  attachments: ApplicationAttachmentView[]
  smartLinks: ApplicationSmartLinkView[]
  proposedEdits: ApplicationEditView[]
  createdAt: string
  updatedAt: string
}

export const APPLICATION_AGGREGATE = "Application"
export function isApplicationStream(streamId: string): boolean {
  return streamId.split("::")[3] === APPLICATION_AGGREGATE
}

/**
 * The AX-2 kebab-case `is-*` lifecycle class for an application status. The def-v2 `ApplicationStatus`
 * values use snake_case (`in_review`, `changes_requested`) but AX-2 mandates kebab-case registered
 * classes (`is-in-review`, `is-changes-requested`). This maps the value → the REGISTERED class so the
 * emitted class is auditor-covered — never inline `is-${status}` (that leaks the snake_case value).
 */
export function applicationStatusClass(status: ApplicationStatus): string {
  return `is-${status.replace(/_/g, "-")}`
}

const provList = (raw: unknown): ProvenanceEntryView[] =>
  Array.isArray(raw)
    ? raw.map((e) => ({ anchor: str((e as Record<string, unknown>)?.["anchor"]), refs: Array.isArray((e as Record<string, unknown>)?.["refs"]) ? ((e as Record<string, unknown>)["refs"] as unknown[]).filter((r): r is string => typeof r === "string") : [] }))
    : []

function gatesList(raw: unknown): ApplicationGateView[] {
  if (!raw || typeof raw !== "object") return []
  return Object.entries(raw as Record<string, { pass?: unknown; notes?: unknown }>).map(([gate, g]) => ({ gate, pass: Boolean(g?.pass), ...(typeof g?.notes === "string" ? { notes: g.notes } : {}) }))
}

function editView(raw: Record<string, unknown>): ApplicationEditView {
  const target = (raw["target"] as Record<string, unknown> | undefined) ?? {}
  return {
    id: str(raw["id"]),
    targetKind: str(target["kind"]),
    ...(typeof target["paragraphIndex"] === "number" ? { paragraphIndex: target["paragraphIndex"] } : {}),
    ...(typeof target["section"] === "string" ? { section: target["section"] } : {}),
    before: str(raw["before"]),
    after: str(raw["after"]),
    status: str(raw["status"], "proposed"),
    ...(typeof raw["rationale"] === "string" ? { rationale: raw["rationale"] } : {}),
  }
}

/** The last critique pass's annotations on the folded trace → the review notes (cold-load path). */
function annotationsFromTrace(trace: Record<string, unknown> | null | undefined): ApplicationAnnotationView[] {
  const passes = Array.isArray(trace?.["passes"]) ? (trace!["passes"] as Record<string, unknown>[]) : []
  const critique = passes.filter((p) => p["kind"] === "critique").at(-1)
  const anns = Array.isArray(critique?.["annotations"]) ? (critique!["annotations"] as Record<string, unknown>[]) : []
  return anns.map((a) => ({ gate: str(a["gate"]), severity: str(a["severity"]), kind: str(a["kind"]), comment: str(a["comment"]), ...(typeof a["span"] === "string" ? { span: a["span"] } : {}) }))
}

/** Project a fully-folded server Application onto the client view (seed via a synthetic Drafted). */
function toApplicationView(app: Record<string, unknown>): ApplicationView | undefined {
  const id = app["id"]
  if (typeof id !== "string") return undefined
  const cl = (app["coverLetter"] as Record<string, unknown> | undefined) ?? {}
  const clMeta = (cl["meta"] as Record<string, unknown> | undefined) ?? {}
  const kurz = app["kurzprofil"] as Record<string, unknown> | null | undefined
  const kurzMeta = (kurz?.["meta"] as Record<string, unknown> | undefined) ?? {}
  const tailoring = (app["cvTailoring"] as Record<string, unknown> | undefined) ?? {}
  const trace = app["generationTrace"] as Record<string, unknown> | null | undefined
  return {
    urn: id as Urn,
    id,
    status: (app["status"] as ApplicationStatus) ?? "draft",
    ...(typeof app["opportunityRef"] === "string" ? { opportunityRef: app["opportunityRef"] } : {}),
    letterBody: str(cl["body"]),
    letterProvenance: provList(cl["provenance"]),
    ...(typeof cl["subjectLine"] === "string" ? { letterSubject: cl["subjectLine"] } : {}),
    ...(typeof cl["salutation"] === "string" ? { letterSalutation: cl["salutation"] } : {}),
    ...(typeof cl["closing"] === "string" ? { letterClosing: cl["closing"] } : {}),
    ...(typeof cl["enclosureLine"] === "string" ? { letterEnclosure: cl["enclosureLine"] } : {}),
    letterDegraded: Boolean(clMeta["degraded"]),
    ...(typeof app["emailNote"] === "string" ? { emailNote: app["emailNote"] } : {}),
    ...(kurz && typeof kurz["body"] === "string" ? { kurzprofilBody: kurz["body"] } : {}),
    kurzprofilProvenance: provList(kurz?.["provenance"]),
    kurzprofilDegraded: Boolean(kurzMeta["degraded"]),
    gates: gatesList(app["qualityGates"]),
    annotations: annotationsFromTrace(trace),
    ...(trace && typeof trace["converged"] === "boolean" ? { converged: trace["converged"] } : {}),
    ...(trace && typeof trace["iterations"] === "number" ? { iterations: trace["iterations"] } : {}),
    ...(trace && typeof trace["stopReason"] === "string" ? { stopReason: trace["stopReason"] } : {}),
    tailoringSections: Array.isArray(tailoring["sections"]) ? (tailoring["sections"] as Record<string, unknown>[]).map((s) => ({ section: str(s["section"]), include: Boolean(s["include"]), ...(typeof s["order"] === "number" ? { order: s["order"] } : {}), ...(typeof s["emphasis"] === "string" ? { emphasis: s["emphasis"] } : {}), ...(typeof s["rewrite"] === "string" ? { rewrite: s["rewrite"] } : {}) })) : [],
    attachments: Array.isArray(app["attachments"]) ? (app["attachments"] as Record<string, unknown>[]).map((a) => ({ assetRef: str(a["assetRef"]), include: Boolean(a["include"]), ...(typeof a["rationale"] === "string" ? { rationale: a["rationale"] } : {}) })) : [],
    smartLinks: Array.isArray(app["smartLinks"]) ? (app["smartLinks"] as Record<string, unknown>[]).map((l) => ({ url: str(l["url"]), kind: str(l["kind"], "other"), include: Boolean(l["include"]), ...(typeof l["label"] === "string" ? { label: l["label"] } : {}) })) : [],
    proposedEdits: Array.isArray(app["proposedEdits"]) ? (app["proposedEdits"] as Record<string, unknown>[]).map(editView) : [],
    createdAt: str(app["createdAt"]),
    updatedAt: str(app["updatedAt"]),
  }
}

/** Commands the application store issues optimistically (names match the api verbs / AX). */
export type ApplicationCommand =
  | { type: "transition"; payload: { applicationId: string; to: ApplicationStatus } }
  | { type: "edit-approve"; payload: { applicationId: string; editId: string } }
  | { type: "edit-reject"; payload: { applicationId: string; editId: string } }

/** Split a letter body into paragraphs the SAME way the server segments (blank-line split). */
function splitParagraphs(body: string): string[] {
  return body.split(/\n{2,}/).map((p) => p.trim()).filter((p) => p.length > 0)
}

function applyApprovedEdit(view: ApplicationView, edit: ApplicationEditView): ApplicationView {
  if (edit.targetKind === "cover_letter_paragraph" && edit.paragraphIndex !== undefined) {
    const paras = splitParagraphs(view.letterBody)
    if (edit.paragraphIndex < 0 || edit.paragraphIndex >= paras.length) return view
    return { ...view, letterBody: paras.map((p, i) => (i === edit.paragraphIndex ? edit.after : p)).join("\n\n") }
  }
  if (edit.targetKind === "cv_tailoring_section" && edit.section) {
    return { ...view, tailoringSections: view.tailoringSections.map((s) => (s.section === edit.section ? { ...s, rewrite: edit.after } : s)) }
  }
  return view
}

export function applicationSpec(transport: CommandTransport): StoreSpec<ApplicationView> {
  return {
    name: "application",
    transport,
    urnOf: (env) => {
      const app = P(env)["application"] as Record<string, unknown> | undefined
      if (app && typeof app["id"] === "string") return app["id"] as Urn
      const id = P(env)["applicationId"]
      return typeof id === "string" ? (id as Urn) : undefined
    },
    appliers: {
      "application.Drafted": (_current, env) => {
        const app = P(env)["application"] as Record<string, unknown> | undefined
        return app ? toApplicationView(app) : undefined
      },
      "application.CoverLetterAuthored": (current, env) => {
        if (!current) return current
        const p = P(env)
        const cl = (p["coverLetter"] as Record<string, unknown> | undefined) ?? {}
        const meta = (cl["meta"] as Record<string, unknown> | undefined) ?? {}
        return {
          ...current,
          letterBody: str(cl["body"], current.letterBody),
          letterProvenance: provList(cl["provenance"]),
          ...(typeof cl["subjectLine"] === "string" ? { letterSubject: cl["subjectLine"] } : {}),
          ...(typeof cl["salutation"] === "string" ? { letterSalutation: cl["salutation"] } : {}),
          ...(typeof cl["closing"] === "string" ? { letterClosing: cl["closing"] } : {}),
          ...(typeof cl["enclosureLine"] === "string" ? { letterEnclosure: cl["enclosureLine"] } : {}),
          letterDegraded: Boolean(meta["degraded"]),
          ...(typeof p["emailNote"] === "string" ? { emailNote: p["emailNote"] } : {}),
        }
      },
      "application.KurzprofilAuthored": (current, env) => {
        if (!current) return current
        const kurz = P(env)["kurzprofil"] as Record<string, unknown> | undefined
        if (!kurz) return current
        const meta = (kurz["meta"] as Record<string, unknown> | undefined) ?? {}
        return { ...current, kurzprofilBody: str(kurz["body"]), kurzprofilProvenance: provList(kurz["provenance"]), kurzprofilDegraded: Boolean(meta["degraded"]) }
      },
      "application.CvTailored": (current, env) => {
        if (!current) return current
        const t = P(env)["cvTailoring"] as Record<string, unknown> | undefined
        if (!t || !Array.isArray(t["sections"])) return current
        return { ...current, tailoringSections: (t["sections"] as Record<string, unknown>[]).map((s) => ({ section: str(s["section"]), include: Boolean(s["include"]), ...(typeof s["order"] === "number" ? { order: s["order"] } : {}), ...(typeof s["emphasis"] === "string" ? { emphasis: s["emphasis"] } : {}), ...(typeof s["rewrite"] === "string" ? { rewrite: s["rewrite"] } : {}) })) }
      },
      "application.QualityGatesEvaluated": (current, env) => {
        if (!current) return current
        const p = P(env)
        const trace = p["trace"] as Record<string, unknown> | undefined
        const anns = Array.isArray(p["annotations"]) ? (p["annotations"] as Record<string, unknown>[]) : []
        return {
          ...current,
          gates: gatesList(p["qualityGates"]),
          annotations: anns.map((a) => ({ gate: str(a["gate"]), severity: str(a["severity"]), kind: str(a["kind"]), comment: str(a["comment"]), ...(typeof a["span"] === "string" ? { span: a["span"] } : {}) })),
          ...(trace && typeof trace["converged"] === "boolean" ? { converged: trace["converged"] } : {}),
          ...(trace && typeof trace["iterations"] === "number" ? { iterations: trace["iterations"] } : {}),
          ...(trace && typeof trace["stopReason"] === "string" ? { stopReason: trace["stopReason"] } : {}),
        }
      },
      "application.AttachmentsUpdated": (current, env) => {
        if (!current) return current
        const raw = P(env)["attachments"]
        return { ...current, attachments: Array.isArray(raw) ? (raw as Record<string, unknown>[]).map((a) => ({ assetRef: str(a["assetRef"]), include: Boolean(a["include"]), ...(typeof a["rationale"] === "string" ? { rationale: a["rationale"] } : {}) })) : current.attachments }
      },
      "application.SmartLinksUpdated": (current, env) => {
        if (!current) return current
        const raw = P(env)["smartLinks"]
        return { ...current, smartLinks: Array.isArray(raw) ? (raw as Record<string, unknown>[]).map((l) => ({ url: str(l["url"]), kind: str(l["kind"], "other"), include: Boolean(l["include"]), ...(typeof l["label"] === "string" ? { label: l["label"] } : {}) })) : current.smartLinks }
      },
      "application.EditProposed": (current, env) => {
        if (!current) return current
        const edit = P(env)["edit"] as Record<string, unknown> | undefined
        return edit ? { ...current, proposedEdits: [...current.proposedEdits, editView(edit)] } : current
      },
      "application.EditApproved": (current, env) => {
        if (!current) return current
        const editId = str(P(env)["editId"])
        const edit = current.proposedEdits.find((e) => e.id === editId)
        if (!edit) return current
        const applied = applyApprovedEdit(current, { ...edit, status: "approved" })
        return { ...applied, proposedEdits: current.proposedEdits.map((e) => (e.id === editId ? { ...e, status: "approved" } : e)) }
      },
      "application.EditRejected": (current, env) => {
        if (!current) return current
        const editId = str(P(env)["editId"])
        return { ...current, proposedEdits: current.proposedEdits.map((e) => (e.id === editId ? { ...e, status: "rejected" } : e)) }
      },
      // ABSOLUTE end-state: a StatusChanged SETS the status (never a relative flip).
      "application.StatusChanged": (current, env) => {
        if (!current) return current
        const to = P(env)["to"]
        return typeof to === "string" ? { ...current, status: to as ApplicationStatus } : current
      },
    },
    projections: {
      alle: (entities) => [...entities.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    },
    optimistic: {
      // Optimistic prediction carries the ABSOLUTE end-state the reconciling event sets.
      transition: (entities, cmd) => {
        const { applicationId, to } = (cmd as ApplicationCommand & { type: "transition" }).payload
        const current = entities.get(applicationId as Urn)
        return current ? [{ urn: applicationId as Urn, next: { ...current, status: to } }] : []
      },
      "edit-approve": (entities, cmd) => {
        const { applicationId, editId } = (cmd as ApplicationCommand & { type: "edit-approve" }).payload
        const current = entities.get(applicationId as Urn)
        if (!current) return []
        const edit = current.proposedEdits.find((e) => e.id === editId)
        if (!edit) return []
        const applied = applyApprovedEdit(current, { ...edit, status: "approved" })
        return [{ urn: applicationId as Urn, next: { ...applied, proposedEdits: current.proposedEdits.map((e) => (e.id === editId ? { ...e, status: "approved" } : e)) } }]
      },
      "edit-reject": (entities, cmd) => {
        const { applicationId, editId } = (cmd as ApplicationCommand & { type: "edit-reject" }).payload
        const current = entities.get(applicationId as Urn)
        return current ? [{ urn: applicationId as Urn, next: { ...current, proposedEdits: current.proposedEdits.map((e) => (e.id === editId ? { ...e, status: "rejected" } : e)) } }] : []
      },
    },
  }
}

export type ApplicationStore = ContextStore<ApplicationView>

// ─── Breadcrumb bus — the shell-level breadcrumb contribution channel (§7a) ────
//
// The shell always renders `App-Space › Workspace`; the ACTIVE surface may
// contribute DEEPER segments (Sub-area, Step) by writing to this bus on attach
// and clearing on detach. Designed extensible: any surface can inject the bus and
// push crumbs (the workshop runner is the first to wire it — Sub-area = workshop
// name, Step = current step with a sibling-step dropdown).

/** One choice inside a crumb's dropdown. A navigable choice sets exactly one of `url` / `stepId`;
 *  a `heading` choice sets neither and renders as a non-interactive group label. */
export interface CrumbChoice {
  /** Display text (user data → rendered `lang="de"` when the crumb is `content`). */
  label: string
  /** Marks the currently-active sibling. */
  current: boolean
  /** Navigate to this client route when chosen (App-Space switch). */
  url?: string
  /** OR scroll to this workshop step when chosen (Sub-area/Step jump). */
  stepId?: string
  /** A parent-group label (§1 grouping) — non-interactive; carries no `url`/`stepId`. */
  heading?: boolean
}

/** One breadcrumb segment. `content` marks user-authored text (renders `lang="de"`). */
export interface Crumb {
  label: string
  /** True when `label` is user/domain data (German), not app chrome. */
  content?: boolean
  /** When present, the crumb becomes a dropdown of sibling choices. */
  choices?: readonly CrumbChoice[]
}

/** The channel a surface uses to contribute deeper breadcrumb segments. */
export interface BreadcrumbBus {
  /** The surface-contributed crumbs (Sub-area, Step, …), reactive. */
  readonly crumbs: ReadonlySignal<readonly Crumb[]>
  set(crumbs: readonly Crumb[]): void
  clear(): void
}

/** Create the singleton breadcrumb bus (wired once at the composition root). */
export function createBreadcrumbBus(): BreadcrumbBus {
  const crumbs = signal<readonly Crumb[]>([])
  return { crumbs, set: (c) => crumbs.set(c), clear: () => crumbs.set([]) }
}

// ─── Section-nav bus — the LEFT-pane within-space navigation channel ──────────
//
// The left pane is CONTEXT-DEPENDENT: it lists the sections of the CURRENT
// app-space (Workshops · Kompass · Suche · Bewerbungen), not the spaces
// themselves (those live in the footer pipeline bar). The shell owns a sensible
// DEFAULT section set per space; the ACTIVE surface may REPLACE it — and attach
// live indicators — by writing to this bus on attach and clearing on detach
// (same lifecycle contract as the BreadcrumbBus). This is the seam a surface uses
// to publish real counts (e.g. Belege → open gaps) instead of the stubbed default.

/** The severity/kind of a section indicator — drives its badge colour. */
export type SectionTone = "info" | "warn" | "critical"

/** A count badge on a section entry: the number + kind/severity of items in that section. */
export interface SectionIndicator {
  /** The count shown in the badge (e.g. number of open gaps). */
  count: number
  /** Severity → colour: info (neutral), warn (amber), critical (red). */
  tone: SectionTone
  /** Optional accessible description of what the count means (e.g. "3 open gaps"). */
  label?: string
}

/** One left-pane section entry — a within-space destination with an optional indicator.
 *  `label` is ALREADY-RESOLVED display text (German): the shell resolves its default
 *  entries through `t()`; a contributing surface resolves its own before pushing. */
export interface SectionNavItem {
  url: string
  /** Active-state path prefix (matches like the footer's `NavItem.match`). */
  match: string
  label: string
  /** Icon key the shell maps to a glyph (so the pane can collapse to an icon rail);
   *  an unknown/absent key renders a neutral dot. */
  icon?: string
  indicator?: SectionIndicator
}

/** The channel a surface uses to REPLACE the left-pane section nav for its space. */
export interface SectionNavBus {
  /** The surface-contributed section entries; empty = the shell renders its per-space default. */
  readonly items: ReadonlySignal<readonly SectionNavItem[]>
  set(items: readonly SectionNavItem[]): void
  clear(): void
  /**
   * LIVE indicator overrides keyed by section URL, MERGED by the shell onto its per-space
   * DEFAULT section items. Distinct from `items` (which fully REPLACES the set): a data
   * owner attaches a live badge to a default section WITHOUT owning the whole section list
   * — and without the shell polling. The shell resolves the badge's i18n label; the owner
   * supplies only `{ count, tone }`. `null` clears a section's indicator. (Belege → open gaps.)
   */
  readonly indicators: ReadonlySignal<Readonly<Record<string, SectionIndicator>>>
  setIndicator(url: string, indicator: SectionIndicator | null): void
}

/** Create the singleton section-nav bus (wired once at the composition root). */
export function createSectionNavBus(): SectionNavBus {
  const items = signal<readonly SectionNavItem[]>([])
  const indicators = signal<Readonly<Record<string, SectionIndicator>>>({})
  return {
    items,
    set: (i) => items.set(i),
    clear: () => items.set([]),
    indicators,
    setIndicator: (url, indicator) => {
      const current = indicators.value
      if (indicator === null) {
        if (!(url in current)) return // already absent — no redundant notification.
        const { [url]: _removed, ...rest } = current
        indicators.set(rest)
        return
      }
      const prev = current[url]
      if (prev && prev.count === indicator.count && prev.tone === indicator.tone) return // unchanged.
      indicators.set({ ...current, [url]: indicator })
    },
  }
}

/** The Belege (evidence) left-pane section URL — the badge target for open gaps (#32). */
export const EVIDENCE_SECTION_URL = "/compass/evidence"

/**
 * Wire a LIVE open-gap indicator onto the Belege section from the evidence store (#32).
 *
 * "Open gaps" = claims graded `gap` that are still `proposed` (awaiting the user) — the
 * honest subset the "{n} offen" badge counts. Publishes `{ count, tone }` to the section-nav
 * bus whenever that count changes; REMOVES the badge at zero (never a fabricated / "0" badge).
 * The publisher lives with the DATA (a reactive `effect` over the evidence store), so the
 * shell never polls; confirming/discarding a gap folds through the store and updates the
 * badge live, without a route change. Returns a disposer.
 *
 * Scope (honest limitation): the evidence store hydrates from the MOUNT-SCOPED push
 * subscription (the Belege ledger / extraction review subscribe the Evidence stream) and
 * RETAINS its entities in memory thereafter — so the badge is accurate app-wide (e.g. while
 * on Profil/Lebenslauf) once Evidence has been observed at least once this session; before
 * that the store is empty and, honestly, no badge shows.
 */
export function publishEvidenceGapIndicator(store: EvidenceStore, bus: SectionNavBus): () => void {
  const claims = store.list("alle")
  return effect([claims], ([list]) => {
    const openGaps = list.filter((c) => c.grade === "gap" && c.status === "proposed").length
    bus.setIndicator(EVIDENCE_SECTION_URL, openGaps > 0 ? { count: openGaps, tone: "warn" } : null)
  })
}

/** The shell's session snapshot (fetched once from the BFF at boot). */
export interface ShellSession {
  user: {
    sub: string
    name?: string
    email?: string
    roles: string[]
    /** The caller's Keycloak organization ALIAS (e.g. `compass-platform`), when known. */
    organizationId?: string
    /** Profile-image URL from the IdP (Google/LinkedIn `picture`), when the token carries it.
     *  Rendered in the sidebar avatar; falls back to initials when absent. */
    picture?: string
  } | null
  /** Dev-login mode is available (non-production BFF). */
  devMode: boolean
  /** Real login is enforced (a confidential client is configured). */
  authEnforced: boolean
}

/**
 * The realm role that ungates the LLM-settings surface AND its nav entry. Retained
 * as the nav item's `requiresRole` marker; the actual gate now also requires the
 * compass-platform org (see {@link sessionCanConfigureLlm}).
 */
export const SETTINGS_GATE_ROLE = "developer"

/** The realm roles that (with the org) unlock LLM configuration — mirrors the api gate. */
export const LLM_SETTINGS_ROLES = ["developer", "admin"] as const
/** The Keycloak org ALIAS that (with a role) unlocks LLM configuration — mirrors the api gate. */
export const LLM_SETTINGS_ORG = "compass-platform"

/** Whether the session holds `role` — the single role derivation. */
export function sessionHasRole(session: ShellSession, role: string): boolean {
  return session.user?.roles.includes(role) ?? false
}

/**
 * Whether the session may configure LLM settings: (`developer` OR `admin`) AND the
 * compass-platform org. This is the CLIENT mirror of the api's `canConfigureLlm` —
 * it drives the nav gated cue + the surface's initial state. The REAL boundary is
 * the server 403 (+ the surface confirms with `llm.capability` from the api).
 */
export function sessionCanConfigureLlm(session: ShellSession): boolean {
  const { user } = session
  if (!user) return false
  const hasRole = LLM_SETTINGS_ROLES.some((role) => user.roles.includes(role))
  return hasRole && user.organizationId === LLM_SETTINGS_ORG
}

/** The AX vocabulary — the machine-checkable DOM contract for this app. */
export const VOCABULARY: AxVocabulary = defineAxVocabulary({
  app: "career",
  stateClasses: [
    "is-current",
    "is-gated",
    "is-pending",
    "is-running",
    "is-completed",
    "is-stopped",
    "is-failed",
    // ingest run lifecycle
    "is-extracting",
    "is-proposed",
    "is-confirmed",
    // evidence claim grade + status
    "is-evidenced",
    "is-self-positioning",
    "is-gap",
    "is-discarded",
    "is-stale",
    // kompass: cv + asset lifecycle
    "is-draft",
    "is-versioned",
    "is-active",
    "is-degraded",
    "is-removing",
    // workshops: gate + step lifecycle
    "is-locked",
    "is-available",
    "is-in-progress",
    "is-accepted",
    "is-skipped",
    "is-selected",
    // workshops: "Dein Weg" path grouping — a step nested under a parent group (§1)
    "is-substep",
    // workshops: recognition-card reactions (flat rows) + findings rail
    "is-chosen",
    "is-nogo",
    "is-most",
    "is-least",
    "is-inferred",
    "is-latest",
    // search: opportunity lifecycle + user verdict
    "is-discovered",
    "is-verified",
    "is-briefed",
    "is-matched",
    "is-bookmarked",
    "is-dismissed",
    // applications: lifecycle states (draft + degraded/pending/running/completed/failed already registered)
    "is-in-review",
    "is-changes-requested",
    "is-approved",
    "is-sent",
    // dev bench-lab — a run card whose newest file write is within the live window.
    "is-live",
  ],
  actVerbs: [
    { verb: "nav", payloadRequired: true },
    { verb: "set-theme", payloadRequired: true },
    // shell chrome — collapse the left sidebar / toggle the right AI-agent pane / dismiss mobile overlays.
    "toggle-nav",
    "toggle-aside",
    "close-panes",
    // shell breadcrumb (§7a) — jump to a sibling workshop step from the crumb dropdown.
    { verb: "breadcrumb-step", payloadRequired: true },
    "trigger-heartbeat",
    "dev-login",
    { verb: "upload-document", payloadRequired: true },
    "start-extraction",
    { verb: "confirm-claim", payloadRequired: true },
    { verb: "discard-claim", payloadRequired: true },
    { verb: "edit-claim", payloadRequired: true },
    { verb: "edit-toggle", payloadRequired: true },
    "confirm-ingest",
    "rederive",
    "author-narrative",
    // kompass
    "fix-version",
    { verb: "set-grade-filter", payloadRequired: true },
    { verb: "set-status-filter", payloadRequired: true },
    { verb: "link-entry", payloadRequired: true },
    { verb: "upload-asset", payloadRequired: true },
    { verb: "confirm-asset", payloadRequired: true },
    { verb: "remove-asset", payloadRequired: true },
    { verb: "confirm-remove-asset", payloadRequired: true },
    { verb: "cancel-remove-asset", payloadRequired: true },
    { verb: "edit-asset", payloadRequired: true },
    { verb: "save-asset", payloadRequired: true },
    { verb: "promote-document", payloadRequired: true },
    "print-cv",
    "upload-portrait",
    // workshops
    { verb: "start-workshop", payloadRequired: true },
    { verb: "goto-step", payloadRequired: true },
    "generate-stimulus",
    { verb: "pick-option", payloadRequired: true },
    { verb: "toggle-veto", payloadRequired: true },
    { verb: "submit-turn", payloadRequired: true },
    { verb: "mark-nuance", payloadRequired: true },
    "consolidate-workshop",
    "update-targeting",
    { verb: "edit-facet", payloadRequired: true },
    { verb: "save-facet", payloadRequired: true },
    { verb: "cancel-facet", payloadRequired: true },
    // workshops: Werte-Sektion (V5) — synthesize/confirm + view-first edit-in-place + AI refine
    "go-cardgame",
    "synthesize",
    "confirm",
    { verb: "toggle-expand", payloadRequired: true },
    { verb: "edit-start", payloadRequired: true },
    { verb: "refine-open", payloadRequired: true },
    "refine-cancel",
    "refine-again",
    { verb: "refine-submit", payloadRequired: true },
    { verb: "refine-accept", payloadRequired: true },
    // search
    "start-search",
    "stop-search",
    { verb: "bookmark", payloadRequired: true },
    { verb: "dismiss", payloadRequired: true },
    { verb: "delete", payloadRequired: true },
    { verb: "open-opportunity", payloadRequired: true },
    // applications
    { verb: "select-opportunity", payloadRequired: true },
    { verb: "generate-application", payloadRequired: true },
    "stop-generation",
    { verb: "open-application", payloadRequired: true },
    { verb: "propose-edit", payloadRequired: true },
    "edit-save",
    "edit-cancel",
    { verb: "edit-approve", payloadRequired: true },
    { verb: "edit-reject", payloadRequired: true },
    { verb: "transition", payloadRequired: true },
    { verb: "package", payloadRequired: true },
    { verb: "download-zip", payloadRequired: true },
    // runs (§8) — cold-read AI-run trace; copy affordances read the prompt from the DOM.
    "reload-runs",
    "copy-prompt",
    "copy-all-prompt",
    // llm settings (§9.1) — per-integration model + params override; sparse save + reset.
    "reload-llm-settings",
    { verb: "save-llm-settings", payloadRequired: true },
    { verb: "reset-llm-settings", payloadRequired: true },
    // credits (Step 5) — buy-credits surface CTA + retry, and the shell's 402 notice dismiss.
    "start-checkout",
    "retry-balance",
    "dismiss-payment-notice",
    // dev bench-lab (§dev) — manual re-poll of the run index / detail.
    "refresh",
  ],
  entitySurfaces: ["career-opportunity-detail", "career-application-review"],
})
